import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { mergeMap, map, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  AdminSetOrganizationAttrsError,
  AdminLoadCurrentOrganizationAttrs,
  AdminSetCurrentOrganizationAttrs,
  AdminLoadStockDigitalPeople,
  AdminSetStockDigitalPeople,
  AdminCreateOrganizationAttrs,
  AdminDeleteOrganizationAttrs,
  AdminLoadDefaultDPs,
  AdminSetDefaultDPs,
} from './admin-organization-attrs.actions';
import { AdminOrganizationAttrsService } from './admin-organization-attrs.service';
import { AdminOrganizationAttrs } from 'src/app/admin/models/organization.model';
import { SetSchemaError } from '../schema/schema.actions';
import { SoulfileService } from '../soulfile/soulfile.service';
import { SoulfileProject } from 'src/app/admin/models/soulfile-project.model';
import {
  AdminOrganizationAttrsApiError,
  AdminOrganizationAttrsApiErrorTypes,
} from './admin-organization-attrs.errors';

@Injectable()
export class AdminOrganizationAttrsEffects {
  getStocks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminLoadStockDigitalPeople),
      switchMap(() =>
        this.organizationAttrsService.getAdminStockDPs().pipe(
          map((stocks) => AdminSetStockDigitalPeople({ stocks })),
          catchError((err) =>
            of(
              AdminSetOrganizationAttrsError({
                error: new AdminOrganizationAttrsApiError(
                  AdminOrganizationAttrsApiErrorTypes.AdminLoadStockDigitalPeopleError,
                  err.error.error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminLoadCurrentOrganizationAttrs),
      mergeMap(({ id }) =>
        this.organizationAttrsService.getAdminOrganizationAttrs(id).pipe(
          map((attrs: AdminOrganizationAttrs) => AdminSetCurrentOrganizationAttrs({ attrs })),
          catchError((error) => of(AdminSetOrganizationAttrsError({ error }))),
        ),
      ),
    ),
  );

  addOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminCreateOrganizationAttrs),
      mergeMap(({ organizationId, values }) =>
        this.organizationAttrsService.addAdminOrganizationAttrs(organizationId, values).pipe(
          map((attrs: AdminOrganizationAttrs) => AdminSetCurrentOrganizationAttrs({ attrs })),
          catchError((error) => of(AdminSetOrganizationAttrsError({ error }))),
        ),
      ),
    ),
  );

  deleteOrganizationAttrs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminDeleteOrganizationAttrs),
      mergeMap(({ id }) =>
        this.organizationAttrsService.deleteAdminOrganizationAttrs(id).pipe(
          map(() => AdminSetCurrentOrganizationAttrs({ attrs: null })),
          catchError((error) => of(AdminSetOrganizationAttrsError({ error }))),
        ),
      ),
    ),
  );

  getDefaultSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminLoadDefaultDPs),
      mergeMap(() =>
        this.soulfileService.loadProjects().pipe(
          map((dps: SoulfileProject[]) => AdminSetDefaultDPs({ dps })),
          catchError((error) => of(SetSchemaError({ error }))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private organizationAttrsService: AdminOrganizationAttrsService,
    private soulfileService: SoulfileService,
  ) {}
}
